import { Component, Input } from '@angular/core';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, EventMemberResponse, CoOrganiserAccessLevel, UserSearchResult } from '../../../../../../../../../../@shared/models/response.module';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-event-member-modal',
  standalone: false,
  templateUrl: './add-member-modal.html',
  styleUrl: './add-member-modal.css'
})
export class AddEventMemberModal {
  @Input() event: EventResponse;
  @Input() existingMemberUserIds: string[] = [];

  userSearch = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearching = false;
  showResults = false;
  private searchSubject = new Subject<string>();

  accessLevel: CoOrganiserAccessLevel = 'limited';
  accessLevelOptions: { value: CoOrganiserAccessLevel; label: string; description: string }[] = [
    { value: 'full', label: 'manageEvent.coOrganisers.accessFull', description: 'manageEvent.coOrganisers.accessFullDesc' },
    { value: 'limited', label: 'manageEvent.coOrganisers.accessLimited', description: 'manageEvent.coOrganisers.accessLimitedDesc' },
    { value: 'view', label: 'manageEvent.coOrganisers.accessView', description: 'manageEvent.coOrganisers.accessViewDesc' }
  ];

  submitted: boolean = false;
  saving: boolean = false;

  constructor(
    private eventsService: EventsService,
    private modal: NgbActiveModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(search => {
      this.performSearch(search);
    });
  }

  onSearchInput(value: string) {
    this.userSearch = value;
    if (value.length >= 2) {
      this.searchSubject.next(value);
    } else {
      this.userSearchResults = [];
      this.showResults = false;
    }
  }

  performSearch(search: string) {
    if (search.length < 2) return;
    this.isSearching = true;
    this.eventsService.searchUsers(search).subscribe({
      next: (res: UserSearchResult[]) => {
        this.userSearchResults = res.filter(u => !this.existingMemberUserIds.includes(u.user_id));
        this.showResults = true;
        this.isSearching = false;
      },
      error: () => {
        this.userSearchResults = [];
        this.isSearching = false;
        this.showResults = false;
      }
    });
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearch = user.full_name;
    this.showResults = false;
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearch = '';
  }

  get isUserInvalid(): boolean {
    return this.submitted && !this.selectedUser;
  }

  get isFormValid(): boolean {
    return !!this.selectedUser && !!this.accessLevel;
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('manageEvent.members.validationError'));
      return;
    }

    this.saving = true;

    this.eventsService.addEventMember({
      event_id: this.event.event_id,
      user_id: this.selectedUser!.user_id,
      access_level: this.accessLevel
    }).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.toastService.success(this.translateService.instant('manageEvent.members.addSuccess'));
        this.modal.close(res);
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.members.addError'));
      }
    });
  }

  close() {
    this.modal.close();
  }
}
